import HttpService from './HttpService';
import APIS from 'src/constants/ApiConfig';
import { ICommonResult, IPageResult, IResultError } from '../entity/BaseResult';
import { mockUrl, mock } from 'src/constants/Mock';
import {
  IMallPharmacyDetail,
  IMallPharmacySearch,
  IMallPricePolicy,
  IMallMedicineSearch,
  IMallPharmacyActive,
  IMallCPMDetail,
  IMallMedicineEdit,
  IMallMedicineType,
  IMallPharmacySettingInfo,
  IMallPharmacySettingData,
  IDoctorMedicineConfig,
  IMedicineCatLog,
  IMallWesternMedicineEdit,
  IWesterMedCheckExistsQuery,
  IProductMapEntity,
} from 'src/entity/MallEntity';
import { IMallCategory, IMallBrand, IMallTCMDetail } from '../entity/MallEntity';
import { ISpuMedicineDetail } from '../entity/SpuEntity';
import { EnumMedicineType, EnumMedicineCategory } from 'src/constants/enum';
import { ITaskResult } from 'src/entity/TaskEntity';
import { ILogistic } from 'src/entity/logistics';
import { message } from 'antd';
import { AxiosResponse } from 'axios';
import { IMedicineCatLogSearch, IWestApproachWay, IWestDosageForm, IWestMedicineFrequency, IWestTakeUnit } from 'src/entity/SearchEntity';

const useMock = false;

interface IExpress {
  name: string;
  value: string;
}

export const SUPPORT_EXPRESSES: ReadonlyArray<IExpress> = [
  {
    name: '顺丰速运',
    value: 'SF',
  },
  {
    name: '百世快递',
    value: 'HTKY',
  },
  {
    name: '中通快递',
    value: 'ZTO',
  },
  {
    name: '申通快递',
    value: 'STO',
  },
  {
    name: '圆通快递',
    value: 'YTO',
  },
  {
    name: '韵达速递',
    value: 'YD',
  },
  {
    name: '邮政快递包裹',
    value: 'YZPY',
  },
  {
    name: 'EMS',
    value: 'EMS',
  },
  {
    name: '天天快递',
    value: 'HHTT',
  },
  {
    name: '京东快递',
    value: 'JD',
  },
  {
    name: '优速快递',
    value: 'UC',
  },
  {
    name: '德邦快递',
    value: 'DBL',
  },
  {
    name: '宅急送',
    value: 'ZJS',
  },
  {
    name: '其他',
    value: 'OTHER',
  },
];

export const getMallUrl = (url: string) => {
  if (useMock) {
    return mockUrl(mock.mall, url);
  } else {
    return APIS.domainApi(url);
  }
};

/**
 * 选中的字段为必填，其他的为不必填
 */
type PickRequired<T, K extends keyof T> = Pick<T, K> & Partial<Omit<T, K>>;

/**
 * mall-药品类型
 */
export const MallMedicineType: IMallMedicineType[] = [
  {
    key: 'med_type_cy',
    value: EnumMedicineType.草药,
    name: '草药',
    category: EnumMedicineCategory.中药,
  },
  {
    key: 'med_type_kl',
    value: EnumMedicineType.颗粒,
    name: '颗粒',
    category: EnumMedicineCategory.中药,
  },
  {
    key: 'med_type_cw',
    value: EnumMedicineType.超微,
    name: '超微',
    category: EnumMedicineCategory.中药,
  },
  {
    key: 'med_type_zcy',
    value: EnumMedicineType.中成药,
    name: '成药',
    category: EnumMedicineCategory.中成药,
  },
  {
    key: 'med_type_fenji',
    value: EnumMedicineType.粉剂,
    name: '粉剂',
    category: EnumMedicineCategory.中药,
  },
  {
    key: 'med_type_sanji',
    value: EnumMedicineType.散剂,
    name: '散剂',
    category: EnumMedicineCategory.中药,
  },
  {
    key: 'med_type_gaofang',
    value: EnumMedicineType.膏方,
    name: '膏方',
    category: EnumMedicineCategory.中药,
  },
  {
    key: 'med_type_nongsuogao',
    value: EnumMedicineType.浓缩膏,
    name: '浓缩膏',
    category: EnumMedicineCategory.中药,
  },
  {
    key: 'med_type_miwan',
    value: EnumMedicineType.蜜丸,
    name: '蜜丸',
    category: EnumMedicineCategory.中药,
  },
  {
    key: 'med_type_shuiwan',
    value: EnumMedicineType.水丸,
    name: '水丸',
    category: EnumMedicineCategory.中药,
  },
  {
    key: 'med_type_nongsuowan',
    value: EnumMedicineType.浓缩丸,
    name: '浓缩丸',
    category: EnumMedicineCategory.中药,
  },
  {
    key: 'med_type_jiaonang',
    value: EnumMedicineType.胶囊,
    name: '胶囊',
    category: EnumMedicineCategory.中药,
  },
];

/**
 * 商城服务
 *
 * @export
 * @class MallService
 */
export class MallService {
  /**
   * 获取药库
   *
   * @static
   * @param {IMallPharmacySearch} query
   * @returns {Promise<IPageResult<IMallPharmacyDetail[]>>}
   * @memberof MallService
   */
  static async searchPharmacy(query: IMallPharmacySearch): Promise<IPageResult<IMallPharmacyDetail[]>> {
    const url = getMallUrl(APIS.mall.pharmacy);
    return HttpService.get(url, query);
  }

  /**
   * 通过UUID获取药库详情
   *
   * @static
   * @param {string} uuid
   * @returns {(Promise<IMallPharmacyDetail | undefined>)}
   * @memberof MallService
   */
  static async getPharmacyByUUID(uuid: string): Promise<IMallPharmacyDetail | undefined> {
    const url = getMallUrl(`${APIS.mall.pharmacy}${uuid}/`);
    return HttpService.get(url);
  }

  /**
   * 创建药库
   *
   * @static
   * @param {IMallPharmacyDetail} data
   * @returns {(Promise<IMallPharmacyDetail | undefined>)}
   * @memberof MallService
   */
  static async createPharmacy(data: Partial<IMallPharmacyDetail>): Promise<IMallPharmacyDetail | undefined> {
    const url = getMallUrl(APIS.mall.pharmacy);
    return HttpService.post(url, {
      name: data.name,
      category: data.category, // 类目CODE
      brand: data.brand, // 品牌UUID
      dosage_form: data.dosage_form,
      order_type: data.order_type,
      active_conditional: data.active_conditional,
    });
  }

  /**
   * 更新药库名称和订单类型
   *
   * @static
   * @param {Partial<IMallPharmacyDetail>} data
   * @returns {(Promise<IMallPharmacyDetail | undefined>)}
   * @memberof MallService
   */
  static async updatePharmacy(data: Partial<IMallPharmacyDetail>): Promise<IMallPharmacyDetail | undefined> {
    const url = getMallUrl(`${APIS.mall.pharmacy}${data.uuid}/`);
    return HttpService.patch(url, {
      name: data.name,
      order_type: data.order_type,
    });
  }

  /**
   * 药库启用true/禁用false
   *
   * @static
   * @param {string} uuid
   * @param {boolean} isActive
   * @returns
   * @memberof MallService
   */
  static async changePharmacyStatus(uuid: string, isActive: boolean): Promise<undefined | IResultError<IMallPharmacyActive>> {
    const url = getMallUrl(`${APIS.mall.pharmacy}${uuid}/active/`);
    return HttpService.patch(url, {
      is_active: isActive,
    });
  }

  /**
   * 删除单元药库
   *
   * @author kai.chen
   * @date 2020-08-05
   * @static
   * @param uuid
   * @returns
   */
  static async deletePharmacy(uuid: string): Promise<boolean> {
    try {
      const url = getMallUrl(`${APIS.mall.pharmacy}${uuid}/`);
      await HttpService.delete(url);
      return true;
    } catch (error) {
      console.error(error);
    }
    return false;
  }

  /**
   * 获取所有类目
   *
   * @static
   * @returns {Promise<IMallCategory[]>}
   * @memberof MallService
   */
  static async getAllMallCategory(): Promise<IPageResult<IMallCategory[]>> {
    const url = getMallUrl(APIS.mall.category);
    return HttpService.get(url, {
      offset: 0,
      limit: 100000,
    });
  }

  /**
   * 获取所有品牌/药厂
   *
   * @static
   * @returns {Promise<IMallBrand[]>}
   * @memberof MallService
   */
  static async getAllMallBrand(): Promise<IPageResult<IMallBrand[]>> {
    const url = getMallUrl(APIS.mall.brand);
    return HttpService.getAll(url, {
      offset: 0,
      limit: 100,
    });
  }

  /**
   * 通过药库UUID获取价格模式
   *
   * @static
   * @param {number} pharmacyUUID
   * @returns {Promise<IMallPricePolicy[]>}
   * @memberof MallService
   */
  static async getMallPricePolicyByPharmacyUUID(pharmacyUUID: string): Promise<IPageResult<IMallPricePolicy[]>> {
    const url = getMallUrl(`${APIS.mall.pricePolicy}`);
    return HttpService.get(url, {
      medicine_group: pharmacyUUID,
      offset: 0,
      limit: 1000000,
    });
  }

  /**
   * 获取中药列表
   *
   * @static
   * @param {IMallMedicineSearch} query
   * @returns {Promise<IPageResult<IMallTCMDetail[]>>}
   * @memberof MallService
   */
  static async searchMedicineDetail(query: IMallMedicineSearch): Promise<IPageResult<IMallTCMDetail[]>> {
    const url = getMallUrl(APIS.mall.medicineSKU);
    return HttpService.get(url, query);
  }

  /**
   * 通过UUID获取中药详情
   *
   * @static
   * @param {string} uuid
   * @returns {(Promise<IMallTCMDetail>)}
   * @memberof MallService
   */
  static async getMedicineDetailByUUID(uuid: string): Promise<IMallTCMDetail> {
    const url = getMallUrl(`${APIS.mall.medicineSKU}${uuid}/`);
    return HttpService.get(url);
  }

  /**
   * 新增中药
   *
   * @static
   * @param {(Partial<IMallMedicineEdit>)} data
   * @returns {(Promise<IMallMedicineEdit>)}
   * @memberof MallService
   */
  static async createMedicine(data: Partial<IMallMedicineEdit>): Promise<IMallMedicineEdit> {
    const url = getMallUrl(`${APIS.mall.medicineSKU}`);
    return HttpService.post(url, data);
  }

  /**
   * 更新中药
   *
   * @static
   * @param {(Partial<IMallMedicineEdit>)} data
   * @returns {(Promise<IMallMedicineEdit>)}
   * @memberof MallService
   */
  static async updateMedicine(data: Partial<IMallMedicineEdit>): Promise<IMallMedicineEdit> {
    const url = getMallUrl(`${APIS.mall.medicineSKU}${data.uuid}/`);
    return HttpService.patch(url, data);
  }

  /**
   * 删除中药/中成药
   *
   * @static
   * @param {string} uuid
   * @returns
   * @memberof MallService
   */
  static async deleteMedicine(uuid: string, category: string | undefined) {
    let url = getMallUrl(`${APIS.mall.medicineSKU}${uuid}/`);
    if (category && category === EnumMedicineCategory.中成药) {
      url = getMallUrl(`${APIS.mall.westernSku}${uuid}/`);
    }
    return HttpService.delete(url);
  }

  /**
   * 药品上架下架
   *
   * @static
   * @param {(Partial<IMallTCMDetail | IMallCPMDetail>)} data
   * @returns {(Promise<IMallTCMDetail | IMallCPMDetail>)}
   * @memberof MallService
   */
  static async changeMedicineActive(data: Partial<IMallTCMDetail | IMallCPMDetail>, category: string | undefined): Promise<{ is_active: boolean }> {
    let url = getMallUrl(`${APIS.mall.medicineSKU}${data.uuid}/`);
    if (category && category === EnumMedicineCategory.中成药) {
      url = getMallUrl(`${APIS.mall.westernSku}${data.uuid}/`);
    }
    return HttpService.patch(url, {
      is_active: !data.is_active,
    });
  }

  /**
   * 药品上架下架
   */
  static async batchMedicineActive(skus: string[], category: string | undefined, is_active: boolean): Promise<ICommonResult<null>> {
    let url = getMallUrl(`${APIS.mall.batchMedicineSKU}`);
    if (category && category === EnumMedicineCategory.中成药) {
      url = getMallUrl(`${APIS.mall.batchWesternSku}`);
    }
    return HttpService.post(url, {
      is_active,
      skus,
    });
  }

  /**
   * 获取脉景品名字典
   *
   * @static
   * @param {stirng} keywords
   * @returns {Promise<IProductMapEntity[]>}
   * @memberof MallService
   */
  static async queryProductMap(keywords: string): Promise<IProductMapEntity[]> {
    const url = getMallUrl(`${APIS.ruleDraft.queryProductMap}?keywords=${keywords}`);
    return HttpService.get(url);
  }

  /**
   * 获取中成药列表
   *
   * @static
   * @param {IMallMedicineSearch} query
   * @returns {Promise<IPageResult<IMallTCMDetail[]>>}
   * @memberof MallService
   */
  static async searchWesternMEdicineDetail(query: IMallMedicineSearch): Promise<ICommonResult<IPageResult<IMallCPMDetail[]>>> {
    const url = getMallUrl(`${APIS.mall.westernSku}`);
    return HttpService.get(url, query);
  }

  /**
   * 通过UUID获取中成成药详情
   *
   * @static
   * @param {string} uuid
   * @returns {(Promise<IMallCPMDetail>)}
   * @memberof MallService
   */
  static async getWesternMedicineDetailByUUID(uuid: string): Promise<IMallCPMDetail> {
    const url = getMallUrl(`${APIS.mall.westernSku}${uuid}/`);
    return HttpService.get(url);
  }

  /**
   * 新增中成药
   *
   * @static
   * @param {(Partial<IMallWesternMedicineEdit>)} data
   * @returns {(Promise<IMallWesternMedicineEdit>)}
   * @memberof MallService
   */
  static async createWesternMedicine(data: Partial<IMallWesternMedicineEdit>): Promise<IMallWesternMedicineEdit> {
    const url = getMallUrl(`${APIS.mall.westernSku}`);
    return HttpService.post(url, data);
  }

  /**
   * 更新中成药
   *
   * @static
   * @param {(Partial<IMallWesternMedicineEdit>)} data
   * @returns {(Promise<IMallWesternMedicineEdit>)}
   * @memberof MallService
   */
  static async updateWesternMedicine(data: Partial<IMallWesternMedicineEdit>): Promise<IMallWesternMedicineEdit> {
    const url = getMallUrl(`${APIS.mall.westernSku}${data.uuid}/`);
    return HttpService.patch(url, data);
  }

  /** 检测中成药是否存在 全名+简称 */
  static async checkExistsWesternMedicine(query: Partial<IWesterMedCheckExistsQuery>): Promise<boolean> {
    const url = getMallUrl(`${APIS.mall.westernSku}check_exists/`);
    try {
      const res: ICommonResult<{ code: boolean; message: string }> = await HttpService.get(url, query);
      if (res.success) {
        return res.data.code;
      }
    } catch (error) {
      console.error(error);
    }
    return false;
  }

  /**
   * 药品有货无货
   *
   * @static
   * @param {(Partial<IMallTCMDetail | IMallCPMDetail>)} data
   * @returns {(Promise<IMallTCMDetail | IMallCPMDetail>)}
   * @memberof MallService
   */
  static async changeMedicineStock(data: Partial<IMallTCMDetail | IMallCPMDetail>): Promise<{ in_stock: boolean }> {
    const url = getMallUrl(`${APIS.mall.medicineSKU}${data.uuid}/`);
    return HttpService.patch(url, {
      in_stock: !data.in_stock,
    });
  }

  /**
   * 获取SPU中药/中成药
   *
   * @static
   * @param {string} categoryCode
   * @returns {Promise<ISpuMedicineDetail[]>}
   * @memberof MallService
   */
  static async getSpuMedicineList(query: { categoryCode?: string }): Promise<IPageResult<ISpuMedicineDetail[]>> {
    const url = getMallUrl(APIS.mall.medicineSPU);
    return HttpService.get(url, {
      category_code: query.categoryCode,
      // category_id: query.categoryId,
      offset: 0,
      limit: 100000,
    });
  }

  /**
   * 药品导入
   *
   * @memberof MallService
   */
  static async importMallMedicine(file: any, pharmacyUUID: string, isAgent: boolean, isOpenSendMedicine: boolean, category: string): Promise<ITaskResult> {
    const url = category === EnumMedicineCategory.中药 ? getMallUrl(APIS.mall.medicineImport) : getMallUrl(APIS.mall.westernMedImport);
    const formData = new FormData();
    const ediEnabled = isAgent ? 'false' : isOpenSendMedicine ? 'true' : 'false';
    formData.append('medicine', file);
    formData.append('medicine_group', `${pharmacyUUID}`);
    formData.append('is_agent', isAgent ? 'true' : 'false');
    formData.append('edi_enabled', ediEnabled);
    return HttpService.post(url, formData);
  }

  /**
   * 西成药药库药品导出数据
   * @returns Blob | undefined
   */
  // static async fetchExportCpmMedicine(medicineGroup: string): Promise<Blob | undefined> {
  static async fetchExportGroupMedicine(medicineGroup: string, category: string): Promise<Blob | undefined> {
    const url = category === EnumMedicineCategory.中药 ? getMallUrl(APIS.mall.medicineExport) : getMallUrl(APIS.mall.westernMedExport);
    return HttpService.get(url, { medicine_group: medicineGroup }, { 'content-type': 'application/octet-stream', responseType: 'arraybuffer' });
  }

  /**
   * 云药房-获取药库设置
   *
   * @static
   * @param {string} buyerUserUUID
   * @returns {Promise<IMallPharmacySettingInfo[]>}
   * @memberof MallService
   */
  static async getCloudPharmacySetting(buyerUserUUID: string): Promise<IMallPharmacySettingInfo[]> {
    let result: IMallPharmacySettingInfo[] = [];
    try {
      const url = getMallUrl(`${APIS.mall.cloudPharmacySetting}?buyer_user=${buyerUserUUID}`);
      result = await HttpService.get(url);
    } catch (error) {
      console.error(error);
    }
    return result;
  }

  /**
   * 云药房-更新药库设置
   *
   * @static
   * @param {string} buyerUserUUID
   * @param {IMallPharmacySettingData[]} settingDataList
   * @returns
   * @memberof MallService
   */
  static async updateCloudPharmacySetting(buyerUserUUID: string, settingDataList: IMallPharmacySettingData[]) {
    try {
      const url = getMallUrl(`${APIS.mall.cloudPharmacySetting}?buyer_user=${buyerUserUUID}`);
      await HttpService.put(url, {
        medicine_groups: settingDataList,
      });
      return true;
    } catch (error) {
      console.error(error);
    }
    return false;
  }

  /**
   * @description 本地药房-获取药库设置
   * @author kai.chen
   * @date 2020-07-17
   * @static
   * @param {string} buyerUserUUID
   * @returns {Promise<IMallPharmacySettingInfo[]>}
   * @memberof MallService
   */
  static async getLocalPharmacySetting(buyerUserUUID: string): Promise<IMallPharmacySettingInfo[]> {
    let result: IMallPharmacySettingInfo[] = [];
    try {
      const url = getMallUrl(`${APIS.mall.localPharmacySetting}?buyer_user=${buyerUserUUID}`);
      result = await HttpService.get(url);
    } catch (error) {
      console.error(error);
    }
    return result;
  }

  /**
   * @description 更新本地药房设置
   * @author kai.chen
   * @date 2020-07-20
   * @static
   * @param {string} buyerUserUUID
   * @param {IMallPharmacySettingData[]} settingDataList
   * @returns
   * @memberof MallService
   */
  static async updateLocalPharmacySetting(buyerUserUUID: string, settingDataList: IMallPharmacySettingData[]) {
    try {
      const tcmListQuery: Array<{
        /** 单元药库uuid, */
        medicine_group: string;
        /** 价格模式uuid */
        medicine_price_policy: string | undefined | null;
        /** 继承单元药库uuid */
        stocking_inherit_from: string | null;
      }> = [];
      const cpmListQuery: Array<{
        /** 单元药库uuid, */
        medicine_group: string;
        /** 价格模式uuid */
        medicine_price_policy: string | undefined | null;
        /** 继承单元药库uuid */
        stocking_inherit_from: string | null;
      }> = [];
      for (const obj of settingDataList) {
        if (!!obj.medicine_group) {
          if (obj.type === EnumMedicineCategory.中成药) {
            cpmListQuery.push({
              medicine_group: obj.medicine_group,
              medicine_price_policy: obj.medicine_price_policy || null,
              stocking_inherit_from: obj.stocking_inherit_from || null,
            });
          } else {
            tcmListQuery.push({
              medicine_group: obj.medicine_group,
              medicine_price_policy: obj.medicine_price_policy || null,
              stocking_inherit_from: obj.stocking_inherit_from || null,
            });
          }
        }
      }
      const tcmUrl = getMallUrl(`${APIS.mall.localPharmacySetting}?buyer_user=${buyerUserUUID}`);
      const cpmUrl = getMallUrl(`${APIS.mall.localCpmPharmacySetting}?buyer_user=${buyerUserUUID}`);
      await HttpService.put(tcmUrl, {
        medicine_groups: tcmListQuery,
      });
      await HttpService.put(cpmUrl, {
        medicine_groups: cpmListQuery,
      });
      return true;
    } catch (error) {
      console.error(error);
    }
    return false;
  }

  /**
   * 获取医生默认药库设置
   *
   * @static
   * @param {string} buyerUserUUID
   * @returns {Promise<IDoctorMedicineConfig>}
   * @memberof MallService
   */
  static async getDoctorMedicineConfig(buyerUserUUID: string): Promise<IDoctorMedicineConfig> {
    const url = getMallUrl(`${APIS.mall.doctorMedicineConfig}?buyer_user=${buyerUserUUID}`);
    return HttpService.get(url);
  }

  /**
   * 更新医生默认药库设置
   *
   * @static
   * @param {string} buyerUserUUID
   * @param {string} medicinePharmacyUUID
   * @param {string} prescriptionPharmacyUUID
   * @returns
   * @memberof MallService
   */
  static async updateDoctorMedicineConfig(buyerUserUUID: string, medicinePharmacyUUID: string | null, prescriptionPharmacyUUID: string | null) {
    try {
      const url = getMallUrl(`${APIS.mall.doctorMedicineConfig}?buyer_user=${buyerUserUUID}`);
      await HttpService.patch(url, {
        prescription_cloud_medicine_group: prescriptionPharmacyUUID,
        order_cloud_medicine_group: medicinePharmacyUUID,
      });
      return true;
    } catch (error) {
      console.error(error);
    }
    return false;
  }

  static async createExpress(data: PickRequired<ILogistic, 'express_company' | 'is_auto_created' | 'goods'>): Promise<ILogistic | undefined> {
    try {
      const url = APIS.domainUrl(APIS.drugStoreOrder) + 'order-express/';
      const res: ILogistic | undefined = await HttpService.post(url, data);
      return res;
    } catch (error) {
      console.error(error);
      const response = error.response as AxiosResponse;

      if (response.status >= 400) {
        let errorTip = '操作失败，请重新再试';
        if (response.status === 400) {
          if (response.data.error_code === 'exceed_max_express_order') {
            errorTip = '操作太频繁，每个订单最天最多只能下5个运单';
          } else if (response.data.error_code === 40001) {
            if (response.data.error_message && response.data.error_message.Reason) {
              errorTip = response.data.error_message.Reason;
            };
          }
        }
        message.error(errorTip);
      }
    }
    return undefined;
  }

  /**
   * @description 获取运单列表
   * @param orderIds 运单对应的商品订单的UUID主键列表
   * @returns {Promise<ILogistic[] | any[]>}
   */
  static async getExpressByOrders(orderIds: string[]): Promise<ILogistic[]> {
    try {
      const url = getMallUrl(APIS.mall.express);
      const res: IPageResult<ILogistic[]> = await HttpService.get(url, {
        limit: Math.max(100, orderIds.length * 5),
        goods__order_no__in: orderIds,
      });
      return res.results || [];
    } catch (error) {
      console.error(error);
      message.error(error.message);
    }

    return [];
  }

  /**
   * @description 获取单个订单详情
   *
   * @param {string} outTradeNo： 处方或门诊订单号
   */
  static async getExpressDetail(logisticNo: string): Promise<ILogistic | undefined> {
    let res: ILogistic | undefined;
    try {
      const url = getMallUrl(`${APIS.mall.express}${logisticNo}/`);
      res = await HttpService.get(url);
    } catch (error) {
      console.error(error);
    }
    return res;
  }

  /**
   *  搜索药典列表数据
   * @param query  分页搜索条件
   * @returns
   */
  static async searchMedicineCatLog(query: IMedicineCatLogSearch): Promise<IMedicineCatLog[]> {
    try {
      const url = getMallUrl(`${APIS.mall.medicineCatLog}`);
      const res: ICommonResult<IPageResult<IMedicineCatLog[]>> = await HttpService.get(url, query);
      if (res.success) {
        return res.data.results || [];
      }
    } catch (error) {
      console.error(error);
    }
    return [];
  }

  /** 获取西/中成药的服用单位 */
  static async fetchTakeUnit(): Promise<IWestTakeUnit[]> {
    try {
      const url = getMallUrl(`${APIS.ruleDraft.westTakeUnit}`);
      const res: ICommonResult<IWestTakeUnit[]> = await HttpService.get(url);
      if (res.success) {
        return res.data || [];
      }
    } catch (error) {
      console.error(error);
    }
    return [];
  }

  /** 获取西成药剂型枚举 */
  static async fetchDosageForm(): Promise<IWestDosageForm[]> {
    try {
      const url = getMallUrl(`${APIS.ruleDraft.westDosageForm}`);
      const res: ICommonResult<IWestDosageForm[]> = await HttpService.get(url);
      if (res.success) {
        return res.data || [];
      }
    } catch (error) {
      console.error(error);
    }
    return [];
  }

  /** 获取西成药给药途径枚举 */
  static async fetchApproachWay(): Promise<IWestApproachWay[]> {
    try {
      const url = getMallUrl(`${APIS.ruleDraft.westDosageRoute}`);
      const res: ICommonResult<IWestApproachWay[]> = await HttpService.get(url);
      if (res.success) {
        return res.data || [];
      }
    } catch (error) {
      console.error(error);
    }
    return [];
  }

  /** 获取西成药用药频次 */
  static async fetchMedicineFrequency(): Promise<IWestMedicineFrequency[]> {
    try {
      const url = getMallUrl(`${APIS.ruleDraft.westMedicationFrequency}`);
      const res: ICommonResult<IWestMedicineFrequency[]> = await HttpService.get(url);
      if (res.success) {
        return res.data || [];
      }
    } catch (error) {
      console.error(error);
    }
    return [];
  }
}



// WEBPACK FOOTER //
// ./src/services/MallService.ts